import { useEffect, useState } from "react";
import { backend_url } from "../../App";
import AddReview from "./AddReview";
import AllReviewsPopup from "./AllReviewsPopup";

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? "text-yellow-400" : "text-gray-200"}>★</span>
    ))}
  </div>
);

const Reviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showAllPopup, setShowAllPopup] = useState(false);

  useEffect(() => {
    fetch(`${backend_url}/api/customerreview`)
      .then((res) => res.json())
      .then((data) => {
        const filtered = data.filter(
          (r) => r.productId === productId && r.showHide === true
        );
        setReviews(filtered);
      })
      .catch((err) => console.error("Failed to load reviews", err));
  }, [productId]);

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div
      className="relative overflow-hidden px-4 sm:px-8 py-12"
      style={{ background: "linear-gradient(180deg, #F6F1FE 0%, #ffffff 100%)" }}
    >
      {/* Top purple line */}
      <div className="absolute top-0 left-0 right-0 h-0.5"
           style={{ background: "linear-gradient(90deg, transparent, #9E79D2, transparent)" }} />

      <div className="relative max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-8 gap-2">
          <span
            className="text-white text-xs font-bold px-4 py-1 rounded-full"
            style={{ background: "linear-gradient(90deg, #8353C6, #9E79D2)" }}
          >
            ⭐ Customer Reviews
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 text-center"
            style={{ fontFamily: "'Times New Roman', Serif" }}
          >
            What Our Customers Say
          </h2>
          <div className="flex items-center gap-3">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-purple-400" />
            <div className="w-1.5 h-1.5 rotate-45 bg-purple-500" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-purple-400" />
          </div>
        </div>

        {/* Stats + action row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8
                        bg-white rounded-2xl border border-purple-100 shadow-sm px-6 py-4">
          {avgRating ? (
            <div className="flex items-center gap-3">
              <span className="text-4xl font-bold text-purple-700">{avgRating}</span>
              <div>
                <StarRating rating={Math.round(avgRating)} />
                <p className="text-xs text-gray-400 mt-0.5">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">No ratings yet</p>
          )}

          <div className="flex gap-3">
            {reviews.length > 4 && (
              <button
                onClick={() => setShowAllPopup(true)}
                className="px-4 py-2 rounded-xl border-2 border-purple-400 text-purple-700 text-sm font-semibold hover:bg-purple-50 transition"
              >
                View All
              </button>
            )}
            <button
              onClick={() => setShowAddPopup(true)}
              className="px-5 py-2 rounded-xl text-white text-sm font-semibold shadow hover:opacity-90 transition"
              style={{ background: "linear-gradient(90deg, #8353C6, #9E79D2)" }}
            >
              ✍️ Write a Review
            </button>
          </div>
        </div>

        {/* Reviews grid or empty state */}
        {reviews.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 bg-white rounded-2xl border border-purple-100">
            <span className="text-5xl">💬</span>
            <p className="text-gray-500 font-medium">No reviews yet.</p>
            <p className="text-purple-600 text-sm font-semibold">Be the first to write a review!</p>
            <button
              onClick={() => setShowAddPopup(true)}
              className="mt-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold shadow"
              style={{ background: "linear-gradient(90deg, #8353C6, #9E79D2)" }}
            >
              Write a Review
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {reviews.slice(0, 4).map((review) => (
              <div
                key={review._id}
                className="flex flex-col gap-2 bg-white rounded-2xl border border-purple-100 p-4 shadow-sm
                           hover:shadow-[0_4px_20px_rgba(124,58,237,0.15)] hover:border-purple-300
                           transition-all duration-300"
              >
                {/* Rating */}
                <StarRating rating={review.rating} />

                {/* Title */}
                <h3 className="font-bold text-sm text-gray-900 leading-snug">
                  {review.reviewTitle.charAt(0).toUpperCase() + review.reviewTitle.slice(1)}
                </h3>

                {/* Body */}
                <p className="text-gray-600 text-xs leading-relaxed flex-1">
                  {review.review.charAt(0).toUpperCase() + review.review.slice(1)}
                </p>

                {/* Image */}
                {review.reviewImage && (
                  <img
                    crossOrigin="anonymous"
                    src={review.reviewImage}
                    alt="Review"
                    className="rounded-xl object-cover w-full h-32 mt-1"
                  />
                )}

                {/* Reviewer */}
                <div className="flex items-center gap-2 mt-auto pt-2 border-t border-purple-50">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                       style={{ background: "linear-gradient(135deg, #8353C6, #9E79D2)" }}>
                    {review.name.charAt(0).toUpperCase()}
                  </div>
                  <p className="text-xs text-purple-600 font-semibold truncate">
                    {review.name.charAt(0).toUpperCase() + review.name.slice(1)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddPopup && <AddReview onClose={() => setShowAddPopup(false)} productId={productId} />}
      {showAllPopup && <AllReviewsPopup reviews={reviews} onClose={() => setShowAllPopup(false)} />}
    </div>
  );
};

export default Reviews;
